import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { auth } from '../firebase';

/**
 * Returns a Set of userIds that the current user has completed
 * the full SuperKonnected ConnectieFlow with (phase === 'completed').
 * 
 * Used to show the SK badge on avatars in the respect system.
 * One query on mount — O(1) lookup per avatar.
 */
export function useCompletedConnections(): Set<string> {
  const [connectedIds, setConnectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    const load = async () => {
      try {
        // Query all connections for this user — filter client-side for completed physical meetings
        // (physicalMeeting.status === 'done' is set when both users submit their reflection)
        const [asUser1, asUser2] = await Promise.all([
          getDocs(query(collection(db, 'connections'), where('user1Id', '==', uid))),
          getDocs(query(collection(db, 'connections'), where('user2Id', '==', uid))),
        ]);

        const ids = new Set<string>();
        // A connection counts as SuperKonnected when the physical meeting is fully done
        const isCompleted = (data: any) =>
          data?.physicalMeeting?.status === 'done' ||
          data?.physicalMeeting?.status === 'completed' ||
          data?.phase === 'completed';

        asUser1.docs.forEach(d => { if (isCompleted(d.data())) ids.add(d.data().user2Id); });
        asUser2.docs.forEach(d => { if (isCompleted(d.data())) ids.add(d.data().user1Id); });
        setConnectedIds(ids);
      } catch (e) {
        console.warn('[useCompletedConnections] failed:', e);
      }
    };

    load();
  }, [auth.currentUser?.uid]);

  return connectedIds;
}
